import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { CategorySidebar } from '@/components/layout/CategorySidebar';
import { MaterialCard } from '@/components/materials/MaterialCard';
import { MaterialList } from '@/components/materials/MaterialList';
import { ColumnPickerButton, OPTIONAL_COLUMNS } from '@/components/materials/ColumnPickerButton';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SectionHeading, WidthSlicerPopover } from '@/components/home/helpers';
import { cn } from '@/lib/utils';
import { ArrowUpDown, Grid, List, Sparkles, Ruler, X } from 'lucide-react';

const DEFAULT_OPTIONAL_COLUMNS = []; // none enabled by default — keeps list dense

const readVisibleCols = () => {
  if (typeof window === 'undefined') return DEFAULT_OPTIONAL_COLUMNS;
  try {
    const raw = window.localStorage.getItem('materials.listColumns');
    if (!raw) return DEFAULT_OPTIONAL_COLUMNS;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_OPTIONAL_COLUMNS;
    return parsed.filter((c) => OPTIONAL_COLUMNS.includes(c));
  } catch (e) {
    return DEFAULT_OPTIONAL_COLUMNS;
  }
};

export const MaterialGridSection = ({
  materials,
  loading,
  selectedCategory,
  setSelectedCategory,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  viewMode,
  setViewMode,
  widthMin,
  setWidthMin,
  widthMax,
  setWidthMax,
  watchlist,
  onWatchlistToggle,
  onSeedData,
  seeding,
}) => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [optionalCols, setOptionalCols] = useState(readVisibleCols);

  useEffect(() => {
    try { window.localStorage.setItem('materials.listColumns', JSON.stringify(optionalCols)); } catch (e) { /* ignore */ }
  }, [optionalCols]);

  const toggleCol = (col) => {
    setOptionalCols((prev) => (prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col]));
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  return (
    <section className="bg-[#FAFAFA] dark:bg-slate-950 flex-1" data-testid="material-grid-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        <SectionHeading
          label={t('home.materials.label')}
          title={t('home.materials.title')}
          subtitle={t('home.materials.subtitle')}
          accentColor="#10B981"
          testId="materials-heading"
        />

        <div className="flex gap-6">
          <div className="hidden lg:block">
            <CategorySidebar
              selectedCategory={selectedCategory}
              onSelectCategory={setSelectedCategory}
              className="h-[calc(100vh-20rem)] sticky top-20"
            />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5 p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-2">
                <span className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  {loading ? '...' : `${materials.length} ${t('home.materialsCount')}`}
                </span>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'gap-2 min-w-[140px] justify-start rounded-none border-slate-300 dark:border-slate-600 h-9',
                        (widthMin || widthMax) && 'border-orange-500 bg-orange-50 dark:bg-orange-950/30'
                      )}
                      data-testid="home-width-slicer-trigger"
                    >
                      <Ruler className="h-4 w-4 shrink-0" />
                      <span className="truncate text-sm">
                        {widthMin || widthMax
                          ? `${widthMin || '0'} – ${widthMax || '2000'} mm`
                          : t('filter.width', 'Breite (mm)')}
                      </span>
                      {(widthMin || widthMax) && (
                        <span
                          role="button"
                          className="ml-auto shrink-0 inline-flex items-center justify-center"
                          data-testid="home-width-slicer-clear-btn"
                          onPointerDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setWidthMin('');
                            setWidthMax('');
                          }}
                        >
                          <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                        </span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-72 p-4 rounded-none border-slate-300 dark:border-slate-600"
                    align="end"
                    data-testid="home-width-slicer-popover"
                  >
                    <WidthSlicerPopover
                      minWidth={widthMin}
                      maxWidth={widthMax}
                      onApply={(min, max) => {
                        setWidthMin(min);
                        setWidthMax(max);
                      }}
                      onClear={() => {
                        setWidthMin('');
                        setWidthMax('');
                      }}
                      t={t}
                    />
                  </PopoverContent>
                </Popover>

                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-40 rounded-none border-slate-300 dark:border-slate-600 h-9" data-testid="sort-select">
                    <ArrowUpDown className="h-4 w-4 mr-2" />
                    <SelectValue placeholder={t('sort.sortBy')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="created_at">{t('sort.dateAdded')}</SelectItem>
                    <SelectItem value="list_price_kEUR">{t('sort.price')}</SelectItem>
                    <SelectItem value="quantity">{t('sort.quantity')}</SelectItem>
                  </SelectContent>
                </Select>

                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-none border-slate-300 dark:border-slate-600 h-9"
                  onClick={() => setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
                  data-testid="sort-order-btn"
                >
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </Button>

                <div className="flex border border-slate-300 dark:border-slate-600">
                  <Button
                    variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('grid')}
                    className="rounded-none h-9"
                    data-testid="view-grid-btn"
                  >
                    <Grid className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('list')}
                    className="rounded-none h-9"
                    data-testid="view-list-btn"
                  >
                    <List className="h-4 w-4" />
                  </Button>
                </div>

                {viewMode === 'list' && (
                  <ColumnPickerButton
                    optionalCols={optionalCols}
                    onToggle={toggleCol}
                    onClear={() => setOptionalCols([])}
                  />
                )}
              </div>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="space-y-3">
                    <Skeleton className="h-48 w-full rounded-none" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                ))}
              </div>
            ) : materials.length > 0 ? (
              viewMode === 'list' ? (
                <MaterialList
                  materials={materials}
                  optionalCols={optionalCols}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                  watchlist={watchlist}
                  onWatchlistToggle={onWatchlistToggle}
                />
              ) : (
                <div
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
                  data-testid="material-grid-view"
                >
                  {materials.map((material) => (
                    <MaterialCard
                      key={material.material_id}
                      material={material}
                      onAddToWatchlist={onWatchlistToggle}
                      isInWatchlist={watchlist.includes(material.material_id)}
                    />
                  ))}
                </div>
              )
            ) : (
              <div className="text-center py-16 border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900">
                <Sparkles className="h-12 w-12 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
                <h3 className="text-lg font-semibold mb-1 dark:text-slate-100">{t('common.noResults')}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
                  {t('home.noMaterials')} {isAdmin && t('home.seedDescription')}
                </p>
                {isAdmin && (
                  <Button
                    onClick={onSeedData}
                    disabled={seeding}
                    className="bg-orange-500 hover:bg-orange-600 rounded-none"
                    data-testid="seed-data-btn"
                  >
                    {seeding ? t('home.seeding') : t('admin.seedData')}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
